import { useSetState } from 'ahooks';
import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useExtraTag } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FromPage, ReferenceModalState } from '@/app/stores/slices/reference-modal';
import type { PostReferenceItem, SortByType, TagType, UpdateShareReferenceInteractionParams } from '@/app/types';
import { reportEvent } from '@/app/utils';
import type { FetchPostReference, MaterialsShareElementsFeedData } from '../types';

const PAGE_SIZE = 20;

interface PostReferenceFeed {
  handleEnter: () => void;
  handleLeave: () => void;
  pageToken?: MutableRefObject<string>;
  updatePageInfo: ({ token, total }: { token?: string; total?: number }) => void;
  fromPage: FromPage;
  fetchData: FetchPostReference;
  fetchMoreMode?: 'token' | 'page';
  pageSize?: number;
}

interface PostReferenceFeedState {
  /** 分类list */
  categories: TagType[];
  /** 排序 */
  sortBy: SortByType;
  /** 当前分类 */
  activeCategory: string;
  /** 列表 */
  referenceList: PostReferenceItem[];
  /** 搜索值 */
  searchValue: string;
}

export const usePostReferenceFeed = ({
  handleEnter,
  handleLeave,
  pageToken,
  updatePageInfo,
  fromPage,
  fetchData,
  fetchMoreMode = 'token',
  pageSize,
}: PostReferenceFeed) => {
  const { allTag, likeTag } = useExtraTag();
  const [state, setState] = useSetState<PostReferenceFeedState>({
    categories: [],
    sortBy: 'recommend',
    activeCategory: allTag.id,
    referenceList: [],
    searchValue: '',
  });

  const topicsRef = useRef<string[]>([]);

  const { openReferenceModalWithData, referenceModalOpen, element, referenceModalUpdateReason } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    referenceModalOpen: state.referenceModalOpen,
    element: state.reference?.element,
    referenceModalUpdateReason: state.referenceModalUpdateReason,
  }));

  useEffect(() => {
    if (referenceModalOpen && element) {
      handleEnter();
    }
    if (!referenceModalOpen) {
      handleLeave();
    }
  }, [referenceModalOpen, element]);

  useEffect(() => {
    if (referenceModalUpdateReason?.type === 'like') {
      const { id, voted } = referenceModalUpdateReason;
      updateShareReferenceInteractionStatus({ id, voted });
    } else if (referenceModalUpdateReason?.type === 'copy') {
      const { id, count } = referenceModalUpdateReason;
      updateShareReferenceInteractionStatus({ id, count });
    } else if (referenceModalUpdateReason?.type === 'collect') {
      const { id, collected } = referenceModalUpdateReason;
      updateShareReferenceInteractionStatus({ id, collected });
    }
  }, [referenceModalUpdateReason]);

  const fetchReq = useCallback((query: MaterialsShareElementsFeedData['query']) => {
    return fetchData({
      query: {
        'pager.page_token': pageToken?.current,
        'pager.pagesz': pageSize || PAGE_SIZE,
        ...query,
      },
    }).then(({ data, error, response }) => {
      if (error) {
        toast({
          type: 'warning',
          message: error.message,
        });
      }
      if (data) {
        if (!pageToken?.current && query?.keyword) {
          reportEvent('search_result', {
            is_success: !!data.share_elements?.length,
            position: fromPage === 'home' ? 'homepage' : 'explore',
          });
        }
        const isFirstPage = fetchMoreMode === 'token' ? !pageToken?.current : !query?.['pager.page'];
        const traceId = response.headers.get('x-md-trace-id') || '';
        setState((prev) => ({
          referenceList: (isFirstPage
            ? data.share_elements || []
            : [...prev.referenceList, ...(data.share_elements || [])]
          ).map((item) => ({
            ...item,
            traceId,
          })),
        }));
        if (data.categories?.length || data.topics?.length) {
          const topics = data.topics || [];
          topicsRef.current = topics.map((it) => it.code || '');
          const categories = [
            allTag, // 全部
            ...(data.has_liked ? [likeTag] : []), // 赞过
          ]
            .concat(
              // 插入主题
              topics.map((it) => ({
                name: it.display_name || '',
                id: it.code || '',
                tag: it.display_tag || '',
              })),
            )
            .concat(
              // 插入接口返回的 categories
              (data.categories || []).map((it) => ({
                name: it.display_name || '',
                id: it.code || '',
                tag: it.display_tag || '',
              })),
            );
          setState({
            categories,
          });
        }
        updatePageInfo({
          token: data.next_page_token || '',
          total: data.total || 0,
        });
      }
    });
  }, []);

  const getShareReferenceData = ({ page }: { page?: number } = {}) => {
    if ((fetchMoreMode === 'token' && !pageToken?.current) || (fetchMoreMode === 'page' && page === 0)) {
      setState({
        referenceList: [],
      });
    }
    const isTopic = topicsRef.current.includes(state.activeCategory);
    return fetchReq({
      topic: isTopic ? state.activeCategory : undefined,
      category: isTopic || [allTag.id, likeTag.id].includes(state.activeCategory) ? undefined : state.activeCategory,
      is_like: state.activeCategory === likeTag.id,
      sort_by: state.sortBy,
      ...(state.searchValue &&
        likeTag.id !== state.activeCategory && {
          keyword: state.searchValue,
        }),
      ...(fetchMoreMode === 'page' && {
        'pager.page': page,
      }),
    });
  };

  const handleDetail = (id: string, from?: ReferenceModalState['referenceModalFrom']) => {
    openReferenceModalWithData({
      referenceModalOpen: true,
      referenceModalMode: 'view',
      referenceModalFrom: from || 'community',
      referenceModalFromPage: fromPage,
      element: {
        id: id,
        modality: 'image',
      },
    });
  };

  const updateShareReferenceInteractionStatus = ({
    id,
    voted,
    count,
    collected,
  }: UpdateShareReferenceInteractionParams) => {
    const index = state.referenceList.findIndex((item) => item.share?.id === id);
    const item = state.referenceList[index];
    if (index !== -1 && item.share) {
      if (typeof voted === 'boolean' && item.share.react_info) {
        item.share.react_info = {
          ...item.share.react_info,
          vote_count: (item.share.react_info?.vote_count ?? 0) + (voted ? 1 : -1),
          voted: voted,
        };
      } else if (typeof count === 'number') {
        item.share.copy_count = count;
      } else if (typeof collected === 'boolean' && item.share.react_info) {
        item.share.react_info = {
          ...item.share.react_info,
          collect_count: (item.share.react_info?.collect_count ?? 0) + (collected ? 1 : -1),
          collected: collected,
        };
      }
      setState({
        referenceList: [...state.referenceList],
      });
    }
  };

  const init = () => {
    setState({
      referenceList: [],
    });
  };

  const onExpose = (index: number) => {
    reportEvent('sharedreference_card_exposure', {
      from_page: fromPage,
      reference_id: state.referenceList[index].element?.id || '',
      type_name: state.categories?.find((it) => it.id === state.activeCategory)?.name || '',
    });
  };

  return {
    getShareReferenceData,
    handleDetail,
    updateShareReferenceInteractionStatus,
    init,
    categories: state.categories,
    activeCategory: state.activeCategory,
    setActiveCategory: (category: string) => {
      if (category === state.activeCategory) {
        return;
      }
      setState({
        activeCategory: category,
      });
    },
    sortBy: state.sortBy,
    setSortBy: (sort: SortByType) => {
      if (sort === state.sortBy) {
        return;
      }
      setState({
        sortBy: sort,
      });
    },
    referenceList: state.referenceList,
    onExpose,
    searchValue: state.searchValue,
    setSearchValue: (value: string) => {
      value &&
        reportEvent('search_click', {
          position: fromPage === 'home' ? 'homepage' : 'explore',
        });
      if (value === state.searchValue) {
        return;
      }
      setState({
        searchValue: value,
      });
    },
  };
};
